import { useStyles } from '@team-griffin/stylr';
import { modifier } from '@ux/style-utils';
import { gu2px, Curvature, Theme } from '@uds/theme-utils';
import { CTA } from 'core/ribbons/cta';
import { Alignment } from 'core/ribbons/primitives';
import { ImageAspectRatio } from 'domain/constants/imageAspectRatio';
import { Dimension } from 'domain/constants/dimension';
import { imageAspectRatioPercentage } from 'presentation/utils/imageAspectRatioPercentage';

export default ({
  theme,
  alignment,
  preserveDimension,
  hasTitle,
  hasBody,
  imageAspectRatio,
  isMinSm,
  ctas,
}: {
  theme: Theme;
  alignment: Alignment;
  preserveDimension: Dimension | string | undefined;
  hasTitle: boolean;
  hasBody: boolean;
  isMinSm: boolean;
  imageAspectRatio: ImageAspectRatio | undefined;
  ctas?: Array<CTA>;
}) => {
  const { spacing, curvature } = theme;
  const hasMultipleCtas = (ctas ?? []).length > 1;

  return useStyles(
    {
      default: {
        root: {
          textAlign: 'left',
          marginBottom: gu2px(1, spacing),
        },
        imageContainer: {
          display: 'flex',
          justifyContent: 'flex-start',
          position: 'relative',
          width: '100%',
          padding: 0,
          overflow: 'hidden',
        },
        // fixed 1:1 size is illustration is provided
        illustration: {
          width: gu2px(16, spacing),
        },
        // 16:9 if imageSrc is provided
        imageBuffer: {
          paddingTop: imageAspectRatioPercentage(imageAspectRatio),
        },
        imageInner: {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
          overflow: 'hidden',
          display: 'flex',
        },
        image: {
          position: 'relative',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '100%',
          height: 'auto',
        },
        ctas: {
          overflow: 'hidden',
          marginTop: gu2px(2, spacing),
        },
        cta: {
          marginTop: gu2px(1, spacing),
        },
      },

      isMinSm: {
        root: {
          marginBottom: gu2px(2, spacing),
        },
      },

      hasAnyText: {
        imageContainer: {
          marginBottom: gu2px(3, spacing),
        },
      },

      [modifier('preserveDimension', Dimension.HEIGHT)]: {
        image: {
          height: '100%',
          width: 'auto',
        },
      },

      [modifier('alignment', Alignment.CENTER)]: {
        root: {
          textAlign: 'center',
        },
        imageInner: {
          justifyContent: 'center',
        },
        imageContainer: {
          justifyContent: 'center',
        },
      },

      [modifier('curvature', Curvature.WEAK)]: {
        imageInner: {
          borderRadius: 3,
        },
      },

      [modifier('curvature', Curvature.STRONG)]: {
        imageInner: {
          borderRadius: 8,
        },
      },
      spaceCtas: {
        cta: {
          marginRight: gu2px(1, spacing),
          display: 'inline-block',
        },
      },
    },
    [
      { isMinSm },
      { curvature },
      { alignment },
      { preserveDimension: preserveDimension?.toUpperCase() },
      { hasAnyText: hasTitle || hasBody },
      { spaceCtas: isMinSm && hasMultipleCtas },
      { imageAspectRatio },
    ],
  );
};
